import SoundUtils from './SoundUtils';

import {hasLatestWebAudioSupport, AudioContext} from '.';

const initialGain = 0.001;
const maxGain = 0.3;

const durationRamp = {
  up: 0.2,
  down: 0.3,
};

// 310ms: just long enough to allow the fade out to complete
// before suspending playback
const fadeTimeout = durationRamp.down * 1000 + 10;

const assetPaths = {
  accessible: ['assets/audio/accessible-hover.wav', 'assets/audio/accessible-active.wav'],
  extensible: ['assets/audio/extensible.wav'],
};

export default class Loop {
  static loopKeys = Object.keys(assetPaths);

  constructor(key) {
    // Since I am only checking for the newest Web Audio support,
    // and therefor not supporting Safari, there is no need to patch any methods
    if (!hasLatestWebAudioSupport) {
      this.play = () => {
        return null;
      };

      // eslint-disable-next-line no-constructor-return
      return null;
    }

    if (!Loop.loopKeys.includes(key)) {
      // eslint-disable-next-line no-constructor-return
      return Error(`The requested sound is not available: ${key}`);
    }

    // Safari seems to have issues with creating the AudioContext right away...
    // will want to look into this when we do support Safari
    this.context = new AudioContext();
    this.trackCount = assetPaths[key].length;
    this.sources = {};
    this.gainNodes = {};
    this.hasStarted = false;

    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
    this.speed = this.speed.bind(this);

    for (let i = 0; i < this.trackCount; i++) {
      this._createGainNodes(i);

      SoundUtils.fetchAudioBuffer(assetPaths[key][i], this.context)
        .then((response) => {
          return this._connectSource(response, i);
        })
        .catch(() => {
          // was this intentional? why not a console.warn?
          return null;
        });
    }
  }

  mute(trackNumber) {
    this.gainNodes[`node${trackNumber}`].gain.exponentialRampToValueAtTime(
      initialGain,
      this.context.currentTime + durationRamp.down,
    );
  }

  unmute(trackNumber) {
    this.gainNodes[`node${trackNumber}`].gain.exponentialRampToValueAtTime(
      maxGain,
      this.context.currentTime + durationRamp.up,
    );
  }

  play() {
    if (!this.hasStarted) {
      for (let i = 0; i < this.trackCount; i++) {
        this.sources[`source${i}`].start();
      }

      // only unmute the first track
      this.unmute(0);
      this.hasStarted = true;

      return;
    }

    this.resume();
  }

  pause() {
    for (let i = 0; i < this.trackCount; i++) {
      this.mute(i);
    }

    // pause the sources only once the fadeOut has completed
    setTimeout(() => {
      this.context.suspend();
    }, fadeTimeout);
  }

  resume() {
    this.context
      .resume()
      .then(() => {
        // resume only the first gainNode
        return this.unmute(0);
      })
      .catch((error) => {
        return console.warn(`Something went wrong with resuming playback: ${error}`);
      });
  }

  speed(speed = 1) {
    const duration = speed > 1 ? durationRamp.up : durationRamp.down;

    for (let i = 0; i < this.trackCount; i++) {
      this.sources[`source${i}`].playbackRate.exponentialRampToValueAtTime(
        speed,
        this.context.currentTime + duration,
      );
    }
  }

  _connectSource(sound, index) {
    if (!sound) {
      return;
    }

    const sourceKey = `source${index}`;

    this.sources[sourceKey] = this.context.createBufferSource();
    this.sources[sourceKey].buffer = sound;
    this.sources[sourceKey].loop = true;

    this.sources[sourceKey]
      .connect(this.gainNodes[`node${index}`])
      .connect(this.context.destination);
  }

  _createGainNodes(index) {
    const gainKey = `node${index}`;

    this.gainNodes[gainKey] = this.context.createGain();
    this.gainNodes[gainKey].gain.setValueAtTime(initialGain, this.context.currentTime);
  }
}
